import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import attendanceService, { AttendanceRecord } from '../../services/api/attendanceService';
import defectService, { DefectBoardRow } from '../../services/api/defectService';
import readFirstRegisterService from '../../services/api/readFirstRegisterService';
import tasksService, { Task } from '../../services/api/tasksService';
import teamService, { TeamListItem } from '../../services/api/teamService';
import timesheetService, { Timesheet, Worker } from '../../services/api/timesheetService';
import projectService, { ProjectListItem } from '../../services/api/projectService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';
import { cachedDefectsBoard, cachedRecords, cachedTasks, mergeById } from '../../services/offline/fieldOfflineForms';
import { filterTeamTasks } from './workerFieldModeUtils';
import { findAuthenticatedWorker } from './workerFieldInsights';

interface WorkerFieldData {
  projectId: string | null;
  projectName: string;
  user: RootState['auth']['user'];
  teamId: string;
  team: TeamListItem | null;
  worker: Worker | null;
  workerLinked: boolean;
  tasks: Task[];
  taskResources: Record<string, TaskMaterial[]>;
  defects: DefectBoardRow[];
  attendance: AttendanceRecord[];
  timesheets: Timesheet[];
  hazards: any[];
  jsas: any[];
  ppeChecks: any[];
  incidents: any[];
  contacts: any[];
  toolboxTalks: any[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface UseWorkerFieldDataOptions {
  workerId?: string | null;
}

const emptyRows = {
  tasks: [] as Task[],
  taskResources: {} as Record<string, TaskMaterial[]>,
  defects: [] as DefectBoardRow[],
  attendance: [] as AttendanceRecord[],
  timesheets: [] as Timesheet[],
  hazards: [] as any[],
  jsas: [] as any[],
  ppeChecks: [] as any[],
  incidents: [] as any[],
  contacts: [] as any[],
  toolboxTalks: [] as any[],
};

const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (months: number) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().slice(0, 10);
};

const firstFulfilled = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback;

const taskAssignedToUser = (task: Task, userId?: string | null) =>
  Boolean(userId && task.assignedToId === userId);

export default function useWorkerFieldData(options: UseWorkerFieldDataOptions = {}): WorkerFieldData {
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const { user } = useSelector((state: RootState) => state.auth);
  const [rows, setRows] = useState(emptyRows);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [fallbackProjects, setFallbackProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const effectiveProjects = projects.length ? projects : fallbackProjects;
  const effectiveProjectId = activeProjectId ?? effectiveProjects[0]?.id ?? null;
  const selectedWorkerId = options.workerId ?? null;
  const selectedWorker = selectedWorkerId ? workers.find((row) => row.id === selectedWorkerId) ?? null : null;
  const worker = useMemo(
    () => selectedWorker ?? findAuthenticatedWorker(workers, user?.firstName, user?.lastName),
    [selectedWorker, user?.firstName, user?.lastName, workers]
  );
  const teamId = selectedWorker?.teamId
    ?? (!selectedWorkerId ? worker?.teamId ?? (user?.teamIds ?? []).filter(Boolean)[0] ?? '' : '');
  const projectName = effectiveProjects.find((project) => project.id === effectiveProjectId)?.name ?? 'Active project';
  const team = teams.find((row) => row.id === teamId) ?? null;

  useEffect(() => {
    if (activeProjectId || projects.length || fallbackProjects.length || !navigator.onLine) return;

    let cancelled = false;
    projectService.getProjects()
      .then((projectRows) => {
        if (!cancelled) setFallbackProjects(projectRows);
      })
      .catch(() => {
        if (!cancelled) setFallbackProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, fallbackProjects.length, projects.length]);

  useEffect(() => {
    if (!effectiveProjectId) {
      setRows(emptyRows);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const cachedAllTaskRows = await cachedTasks(effectiveProjectId);
        const cachedTaskRows = teamId
          ? filterTeamTasks(cachedAllTaskRows, teamId)
          : selectedWorkerId
            ? cachedAllTaskRows
            : cachedAllTaskRows.filter((task) => taskAssignedToUser(task, user?.id));
        const cachedAttendance = (await cachedRecords<AttendanceRecord>('attendance_record', effectiveProjectId))
          .filter((row) => !teamId || row.worker?.teamId === teamId);
        const cachedTimesheets = (await cachedRecords<Timesheet>('timesheet', effectiveProjectId))
          .filter((row) => !teamId || row.teamId === teamId);
        const cachedDefects = (await cachedDefectsBoard(effectiveProjectId, { activeOnly: false })).defects;
        const cachedTeamRows = (await cachedRecords<TeamListItem>('team')).filter((row) => !teamId || row.id === teamId);
        const cachedWorkerRows = (await cachedRecords<Worker>('worker')).filter((row) => !teamId || row.teamId === teamId);

        if (!navigator.onLine) {
          if (!cancelled) {
            setTeams(cachedTeamRows);
            setWorkers(cachedWorkerRows);
            setRows((current) => ({
              ...current,
              tasks: cachedTaskRows,
              attendance: cachedAttendance,
              timesheets: cachedTimesheets,
              defects: cachedDefects,
            }));
          }
          return;
        }

        const [
          tasksResult,
          teamResult,
          workersResult,
          timesheetsResult,
          attendanceResult,
          defectsResult,
          hazardsResult,
          jsasResult,
          ppeResult,
          incidentsResult,
          contactsResult,
          toolboxResult,
        ] = await Promise.allSettled([
          tasksService.getTasks({
            projectId: effectiveProjectId,
            teamId: teamId || undefined,
            workerId: selectedWorkerId || undefined,
            limit: 150,
          }),
          teamId ? teamService.getTeam(teamId).then((row) => [row]) : teamService.getTeams(),
          timesheetService.listWorkers({ teamId: teamId || undefined, active: true }),
          timesheetService.list({ projectId: effectiveProjectId, teamId: teamId || undefined, from: monthsAgo(12), to: today() }),
          attendanceService.list({ projectId: effectiveProjectId, teamId: teamId || undefined }),
          defectService.getDefectsBoard(effectiveProjectId, { teamId: teamId || undefined, activeOnly: false }),
          readFirstRegisterService.list('/hazard-assessments', { projectId: effectiveProjectId }),
          readFirstRegisterService.list('/jsas', { projectId: effectiveProjectId }),
          readFirstRegisterService.list('/ppe-checks', { projectId: effectiveProjectId }),
          readFirstRegisterService.list('/incidents', { projectId: effectiveProjectId }),
          readFirstRegisterService.list('/emergency-contacts', { projectId: effectiveProjectId }),
          readFirstRegisterService.list('/toolbox-talks', { projectId: effectiveProjectId }),
        ]);

        if (cancelled) return;
        const serverTasks = firstFulfilled(tasksResult, { tasks: [], total: 0, page: 1, limit: 150, totalPages: 1 }).tasks;
        const pendingLocalTasks = cachedTaskRows.filter((task) => task.id.startsWith('local:'));
        const loadedTasks = mergeById(serverTasks, pendingLocalTasks);
        const userAssignedTasks = selectedWorkerId ? [] : loadedTasks.filter((task) => taskAssignedToUser(task, user?.id));
        const visibleTasks = userAssignedTasks.length
          ? userAssignedTasks
          : teamId
            ? loadedTasks
            : selectedWorkerId
              ? loadedTasks
              : [];
        const visibleTaskIds = new Set(visibleTasks.map((task) => task.id));
        const resourceResults = await Promise.allSettled(
          visibleTasks.slice(0, 25).map((task) => taskMaterialsService.listForTask(task.id))
        );
        if (cancelled) return;
        const taskResources: Record<string, TaskMaterial[]> = {};
        visibleTasks.slice(0, 25).forEach((task, index) => {
          const result = resourceResults[index];
          if (result.status === 'fulfilled' && result.value.length) {
            taskResources[task.id] = result.value;
          }
        });
        const loadedDefects = defectsResult.status === 'fulfilled' ? defectsResult.value.defects : cachedDefects;
        const visibleDefects = loadedDefects.filter((defect) =>
          visibleTaskIds.has(defect.originalTaskId) || Boolean(teamId && defect.assignedToTeamId === teamId)
        );

        setTeams(firstFulfilled(teamResult, cachedTeamRows));
        setWorkers(firstFulfilled(workersResult, cachedWorkerRows));
        setRows({
          tasks: visibleTasks,
          taskResources,
          defects: visibleDefects,
          attendance: mergeById(firstFulfilled(attendanceResult, []), cachedAttendance),
          timesheets: mergeById(firstFulfilled(timesheetsResult, []), cachedTimesheets),
          hazards: firstFulfilled(hazardsResult, []),
          jsas: firstFulfilled(jsasResult, []),
          ppeChecks: firstFulfilled(ppeResult, []),
          incidents: firstFulfilled(incidentsResult, []),
          contacts: firstFulfilled(contactsResult, []),
          toolboxTalks: firstFulfilled(toolboxResult, []),
        });
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Worker field data could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [effectiveProjectId, reloadToken, selectedWorkerId, teamId, user?.id]);

  const scopedAttendance = useMemo(
    () => (worker ? rows.attendance.filter((record) => record.workerId === worker.id) : rows.attendance),
    [rows.attendance, worker]
  );
  const scopedTimesheets = useMemo(
    () => worker
      ? rows.timesheets.map((sheet) => ({
          ...sheet,
          entries: (sheet.entries ?? []).filter((entry) => entry.workerId === worker.id),
        })).filter((sheet) => (sheet.entries ?? []).length > 0)
      : rows.timesheets,
    [rows.timesheets, worker]
  );

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return {
    projectId: effectiveProjectId,
    projectName,
    user,
    teamId,
    team,
    worker,
    workerLinked: Boolean(worker),
    tasks: rows.tasks,
    taskResources: rows.taskResources,
    defects: rows.defects,
    attendance: scopedAttendance,
    timesheets: scopedTimesheets,
    hazards: rows.hazards,
    jsas: rows.jsas,
    ppeChecks: rows.ppeChecks,
    incidents: rows.incidents,
    contacts: rows.contacts,
    toolboxTalks: rows.toolboxTalks,
    loading,
    error,
    reload,
  };
}
